import { PageHeader } from "../../components/shared/PageHeader";
import { Badge } from "../../components/shared/Badge";
import { GitMerge, LayoutTemplate, BarChart2, Lock } from "lucide-react";
import { useEnterpriseModules } from "@platform/hooks";

const MODULE_META: Record<string, { icon: React.ComponentType<{ className?: string }>; iconBg: string }> = {
  migrate: { icon: GitMerge, iconBg: "bg-indigo-600" },
  design: { icon: LayoutTemplate, iconBg: "bg-cyan-600" },
  analytics: { icon: BarChart2, iconBg: "bg-gray-400" },
};

interface EnterpriseModule {
  moduleId: string; moduleKey: string; moduleName: string;
  description: string | null; isAvailable: boolean;
  isEnabled: boolean; isProvisioned: boolean;
}

const PERM_LABELS: Record<string, string[]> = {
  migrate: ["View formulas", "Lock files", "Submit to queue", "GitHub sync"],
  design: ["View tasks", "Create tasks", "Generate HDL", "Export documents"],
  analytics: ["View dashboards", "Export reports"],
};

export function EnterpriseAppAccessPage() {
  const { data: modules = [], isLoading } = useEnterpriseModules();
  const typedModules = modules as EnterpriseModule[];

  return (
    <div className="space-y-5">
      <PageHeader
        title="App access"
        subtitle="Modules available to your enterprise."
      />

      {isLoading ? (
        <div className="p-8 text-center text-sm text-ds-text-muted">Loading modules...</div>
      ) : (
        <div className="space-y-3">
          {typedModules.map((mod) => {
            const meta = MODULE_META[mod.moduleKey] ?? { icon: BarChart2, iconBg: "bg-gray-400" };
            const Icon = meta.icon;
            const perms = PERM_LABELS[mod.moduleKey] ?? [];

            return (
              <div key={mod.moduleId}
                className={`bg-white border rounded-card shadow-card p-5 transition-colors ${mod.isEnabled ? "border-indigo-200" : "border-ds-border"}`}>
                <div className="flex items-start gap-4">
                  <div className={`w-10 h-10 rounded-xl ${meta.iconBg} flex items-center justify-center flex-shrink-0 mt-0.5`}>
                    {!mod.isAvailable ? <Lock className="w-4 h-4 text-white" /> : <Icon className="w-5 h-5 text-white" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-semibold text-ds-text">{mod.moduleName}</p>
                        {!mod.isAvailable && <Badge variant="gray"><Lock className="w-2.5 h-2.5 mr-1" />Not available</Badge>}
                      </div>
                      <div className="flex items-center gap-3 flex-shrink-0">
                        <Badge variant={mod.isEnabled ? "green" : "gray"}>
                          {mod.isEnabled ? "Enabled" : "Disabled"}
                        </Badge>

                      </div>
                    </div>
                    <p className="text-xs text-ds-text-muted mt-1">{mod.description}</p>

                    {/* Permission summary — visible to all users */}
                    {mod.isEnabled && perms.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-ds-border">
                        <p className="text-xs font-medium text-ds-text-secondary mb-2">What you can do in this module:</p>
                        <div className="flex flex-wrap gap-1.5">
                          {perms.map((p) => (
                            <span key={p} className="text-xs px-2 py-0.5 bg-ds-bg border border-ds-border rounded text-ds-text-muted">{p}</span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}


    </div>
  );
}
